import { useReducer, useEffect, Reducer, useCallback } from 'react';
import keyBy from 'lodash/keyBy';

export const OPTIONS = {
  abortEarly: false
};

type OptimisticEntity<T> = T; // returns sync before completed dispatch
type FormChangeResponse<T> = {
  optimisticEntity: OptimisticEntity<T>;
};
type UseFormOptions<T> = {
  initialFormValue: T | null;
  // validationSchema?: Yup.ObjectSchema;
  validationSchema?: any;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  validateOnLoad?: boolean;
  returnOptimisticEntityOnChange?: boolean;
  keyBy?: string;
};

// export type UseForm<Entity> = [
//   UseFormState<Entity> & { isValid: boolean; cachedForm?: Entity | null },
//   UseFormDispatch<Entity>
// ];

export type UseForm = any;

type ErrorMap = { [key: string]: { [key: string]: string } };
export type MaybeErrorMap = ErrorMap | null;

export interface UseFormState<Entity> {
  form: Entity;
  focusedField: string | undefined | null;
  touchedFieldsMap: { [key: string]: Set<string> };
  dirtyFieldsMap: { [key: string]: Set<string> };
  errors: MaybeErrorMap;
  cachedForm: Entity | null;
  isDirty: boolean;
  isTouched: boolean;
  isFocused: boolean;
}

export interface ValidateFormOptions {
  onlyDirtyFields?: boolean;
  isMap?: boolean;
}

export interface SetFormOptions {
  shouldCache?: boolean;
  keyBy?: string;
}

export interface UseFormDispatch<Entity> {
  onFormChange: (
    key: string | number
  ) => (name: string, value: any) => FormChangeResponse<Entity> | undefined;
  onFocus: (key: string | number) => (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  setForm: (nextFormValue: Entity | Entity[], options?: SetFormOptions) => void;
  setFormErrors: (errors: MaybeErrorMap) => void;
  validateForm: (formValue?: Entity, options?: ValidateFormOptions) => Promise<MaybeErrorMap>;
  validateSingleForm: (
    key: string,
    formValue: any,
    options?: ValidateFormOptions
  ) => Promise<MaybeErrorMap>;
  revertForm: () => void;
}

type Action<Entity> =
  | {
      type: 'FORM_CHANGED';
      payload: { name: string; value: string; key: string };
    }
  | { type: 'ON_FOCUS'; payload: { name: string | null; key: string | number } }
  | { type: 'ON_FORM_BLUR' }
  | { type: 'REVERT_FORM' }
  | { type: 'SET_FORM'; payload: { nextFormValue: Entity; shouldCache?: boolean; keyBy?: string } }
  | { type: 'SET_FORM_ERRORS'; payload: MaybeErrorMap };

const getInitialState = <Entity>(): UseFormState<Entity> => ({
  form: {} as Entity,
  focusedField: null,
  touchedFieldsMap: {},
  dirtyFieldsMap: {},
  errors: null,
  cachedForm: null,
  isDirty: false,
  isTouched: false,
  isFocused: false
});

const formReducer = <Entity extends Record<string, unknown>>(
  state: UseFormState<Entity>,
  action: Action<Entity>
): UseFormState<Entity> => {
  switch (action.type) {
    case 'FORM_CHANGED': {
      if (
        state.cachedForm &&
        (state.cachedForm as any)[action.payload.key]?.[action.payload.name] ===
          action.payload.value
      ) {
        state.dirtyFieldsMap[action.payload.key]?.delete(action.payload.name);
        if (state.dirtyFieldsMap[action.payload.key]?.size === 0) {
          delete state.dirtyFieldsMap[action.payload.key];
        }
      } else {
        if (!state.dirtyFieldsMap?.[action.payload.key]) {
          state.dirtyFieldsMap[action.payload.key] = new Set([action.payload.name]);
        } else {
          state.dirtyFieldsMap[action.payload.key]?.add(action.payload.name);
        }
      }

      const keyedFormValue: any = state.form[action.payload.key];
      const nextFormValue = {
        ...state.form,
        [action.payload.key]: {
          ...keyedFormValue,
          [action.payload.name]: action.payload.value
        }
      };

      return {
        ...state,
        form: nextFormValue
      };
    }
    case 'ON_FOCUS': {
      const keyedByName = `${action.payload.key}:${action.payload.name}`;

      if (state.touchedFieldsMap) {
        state.touchedFieldsMap[String(action.payload.key)]?.add(keyedByName);
      }

      return {
        ...state,
        focusedField: keyedByName === state.focusedField ? null : keyedByName
      };
    }
    case 'ON_FORM_BLUR':
      return {
        ...state,
        focusedField: null
      };
    case 'SET_FORM': {
      const nextFormValue = action.payload.nextFormValue;
      console.log('nextFormValue: ', nextFormValue);
      return {
        ...state,
        form: nextFormValue,
        ...(action.payload.shouldCache && { cachedForm: { ...nextFormValue } })
      };
    }
    case 'SET_FORM_ERRORS':
      return {
        ...state,
        errors: action.payload
      };
    case 'REVERT_FORM':
      return {
        ...getInitialState<Entity>(),
        form: { ...state.cachedForm } as Entity
      };
    default:
      return getInitialState<Entity>();
  }
};

const useFormMap = <Entity extends Record<string, unknown>>({
  initialFormValue,
  validateOnChange = false,
  validateOnBlur = false,
  validateOnLoad = false,
  validationSchema,
  returnOptimisticEntityOnChange = false
}: UseFormOptions<Entity>): UseForm => {
  const [state, dispatch] = useReducer<Reducer<UseFormState<Entity>, Action<Entity>>>(
    formReducer,
    getInitialState<Entity>()
  );

  useEffect(() => {
    if (initialFormValue) setForm(initialFormValue, { shouldCache: true });
    if (validateOnLoad && initialFormValue) validateForm(initialFormValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFormErrors = useCallback(
    (errors: MaybeErrorMap): void => dispatch({ type: 'SET_FORM_ERRORS', payload: errors }),
    [dispatch]
  );

  const buildFormMapErrorMap = useCallback(
    async (key: string, formValue: any, options: ValidateFormOptions) => {
      let errorMap;
      try {
        await validationSchema?.validate(formValue, OPTIONS);
      } catch (e) {
        const err = e as any;
        const errs = Array.isArray(err?.inner) ? err.inner : [err.inner];

        errorMap = errs.reduce((acc: any, x: any) => {
          const includedError = { ...acc, [x.path]: x.message };

          if (options.onlyDirtyFields) {
            return state.dirtyFieldsMap?.[key]?.has(x.path)
              ? includedError
              : state.touchedFieldsMap?.[key]?.has(x.path)
              ? includedError
              : acc;
          }

          return includedError;
        }, errorMap);
      }

      return errorMap;
    },
    [validationSchema, state]
  );

  const validateSingleForm = async (
    key: string,
    formValue: Entity,
    options: ValidateFormOptions = {}
  ) => {
    const errorMap = await buildFormMapErrorMap(key, formValue, options);

    if (errorMap) setFormErrors({ [key]: { ...errorMap } });

    return errorMap;
  };

  const validateForm = useCallback(
    async (formValue?: Entity, options = {} as ValidateFormOptions): Promise<MaybeErrorMap> => {
      if (validationSchema) {
        let errorMap: any;

        if (!Array.isArray(formValue) && typeof formValue === 'object') {
          for (const [key, value] of Object.entries(formValue)) {
            const keyedErrorMap = await buildFormMapErrorMap(key, value, options);
            if (keyedErrorMap) {
              errorMap = { ...errorMap, [key]: { ...keyedErrorMap } };
            }
          }
        }

        setFormErrors({ ...errorMap });
        return errorMap;
      }

      setFormErrors(null);
      return null;
    },
    [setFormErrors, validationSchema, buildFormMapErrorMap]
  );

  const onFormChange = useCallback(
    (key: string | number) =>
      (name: string, value: string): FormChangeResponse<Entity> | undefined => {
        const action: Action<Entity> = {
          type: 'FORM_CHANGED',
          payload: { name, value, key: String(key) }
        };

        if (returnOptimisticEntityOnChange || validateOnChange) {
          dispatch(action);

          const optimisticEntity = {
            ...state.form,
            [key]: { ...(state.form[key] as any), [name]: value }
          };
          if (validateOnChange)
            validateForm(optimisticEntity, { onlyDirtyFields: true, isMap: true });

          return {
            optimisticEntity: optimisticEntity
          };
        }

        dispatch(action);
        return undefined;
      },
    [dispatch, validateForm, state, validateOnChange, returnOptimisticEntityOnChange]
  );

  const onFocus = useCallback(
    (key: string | number) =>
      (e: React.FocusEvent<HTMLInputElement>): void => {
        const { name } = e.target;

        dispatch({ type: 'ON_FOCUS', payload: { name, key } });
      },
    [dispatch]
  );

  const onBlur = useCallback(() => {
    const action: Action<Entity> = { type: 'ON_FORM_BLUR' };
    const optimisticState = formReducer(state, action);

    dispatch(action);

    if (validateOnBlur) validateForm(optimisticState.form, { onlyDirtyFields: true, isMap: true });
  }, [dispatch, state, validateForm, validateOnBlur]);

  /* @param nextFormValue should already be keyed (if applicable) */
  const setForm = useCallback(
    (nextFormValue, { shouldCache = false, keyBy: keyByProperty } = {} as any): void => {
      let value = nextFormValue;
      if (Array.isArray(nextFormValue) && keyByProperty) {
        value = keyBy(value, keyByProperty);
      }
      dispatch({ type: 'SET_FORM', payload: { nextFormValue: value, shouldCache: !!shouldCache } });
    },
    [dispatch]
  );

  const revertForm = useCallback(() => dispatch({ type: 'REVERT_FORM' }), [dispatch]);

  const isDirty = Object.keys(state.dirtyFieldsMap).length > 0;
  const isTouched = Object.keys(state.touchedFieldsMap).length > 0;

  return [
    {
      ...state,
      isValid: !Object.keys(state.errors || {}).length,
      isDirty,
      isTouched: isDirty || isTouched, // tries to figure out even if 'focus' handler not used
      isFocused: !!state.focusedField
    },
    {
      onFormChange,
      onFocus,
      onBlur,
      setForm,
      revertForm,
      setFormErrors,
      validateForm,
      validateSingleForm
    }
  ];
};

export default useFormMap;
